import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';

export interface AppUser {
  id: string;
  auth_user_id: string | null;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  email: string | null;
  avatar_url: string | null;
  active: boolean;
  twilio_phone_number: string | null;
  system_role_id: string | null;
  department_id: string | null;
  manager_id: string | null;
  can_view_all_departments: boolean;
  created_at: string;
  updated_at: string;
  system_role?: {
    id: string;
    name: string;
    code: string;
  };
  department?: {
    id: string;
    name: string;
    code: string;
  };
  manager?: {
    id: string;
    display_name: string;
  };
  accessible_departments?: Array<{
    department: {
      id: string;
      name: string;
    };
  }>;
}

export function useAppUsers() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [emailGroupMap, setEmailGroupMap] = useState<Map<string, string[]>>(new Map());
  const { user: currentUser } = useAuth();

  const loadUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch all app users with relationships (manager via join to avoid N+1)
      const { data: allUsers, error: fetchError } = await supabase
        .from('app_users')
        .select(`
          *,
          system_role:system_roles(id, name, code),
          department:departments(id, name, code),
          accessible_departments:user_department_access(
            department:departments(id, name)
          ),
          manager:app_users!manager_id(id, display_name, first_name, last_name)
        `)
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;
      
      // Fetch user identity links to deduplicate
      const { data: links } = await supabase
        .from('user_identity_links')
        .select('legacy_user_id, current_user_id');
      
      // Create a map of legacy -> current user IDs
      const legacyToCurrent = new Map<string, string>();
      (links || []).forEach(link => {
        legacyToCurrent.set(link.legacy_user_id, link.current_user_id);
      });
      
      // Filter out legacy users that are linked to a current user
      const deduplicatedUsers = (allUsers || []).filter((user: any) => 
        !legacyToCurrent.has(user.id)
      );
      
      // Fallback deduplication by email (for users without identity links)
      const emailGroupMap = new Map<string, string[]>();
      const emailToWinner = new Map<string, any>();
      
      deduplicatedUsers.forEach((user: any) => {
        if (!user.email) return;
        const normalizedEmail = user.email.trim().toLowerCase();
        
        if (!emailToWinner.has(normalizedEmail)) {
          emailToWinner.set(normalizedEmail, user);
          emailGroupMap.set(user.id, [user.id]);
        } else {
          const currentWinner = emailToWinner.get(normalizedEmail);
          const allIds = emailGroupMap.get(currentWinner.id) || [currentWinner.id];
          allIds.push(user.id);
          emailGroupMap.set(currentWinner.id, allIds);
          
          // Determine if this user should be the new winner
          let shouldReplace = false;
          
          // Priority 1: User referenced as current_user_id in links
          const isCurrentInLinks = (links || []).some((l: any) => l.current_user_id === user.id);
          const winnerIsCurrentInLinks = (links || []).some((l: any) => l.current_user_id === currentWinner.id);
          
          if (isCurrentInLinks && !winnerIsCurrentInLinks) {
            shouldReplace = true;
          } else if (!isCurrentInLinks && !winnerIsCurrentInLinks) {
            // Priority 2: User with auth_user_id
            if (user.auth_user_id && !currentWinner.auth_user_id) {
              shouldReplace = true;
            } else if (user.auth_user_id === currentWinner.auth_user_id) {
              // Priority 3: More complete name fields
              const userNameScore = (user.display_name ? 3 : 0) + (user.first_name ? 1 : 0) + (user.last_name ? 1 : 0);
              const winnerNameScore = (currentWinner.display_name ? 3 : 0) + (currentWinner.first_name ? 1 : 0) + (currentWinner.last_name ? 1 : 0);
              
              if (userNameScore > winnerNameScore) {
                shouldReplace = true;
              } else if (userNameScore === winnerNameScore) {
                // Priority 4: Most recently updated
                if (new Date(user.updated_at) > new Date(currentWinner.updated_at)) {
                  shouldReplace = true;
                }
              }
            }
          }
          
          if (shouldReplace) {
            emailGroupMap.delete(currentWinner.id);
            emailGroupMap.set(user.id, allIds);
            emailToWinner.set(normalizedEmail, user);
            
            if (process.env.NODE_ENV === 'development') {
              console.log(`Deduplicated by email fallback: ${normalizedEmail} (${allIds.length} accounts merged)`);
            }
          }
        }
      });
      
      // Keep only winners
      const uniqueByEmailUsers = deduplicatedUsers.filter((user: any) => 
        !user.email || emailToWinner.get(user.email.trim().toLowerCase())?.id === user.id
      );
      
      setUsers(uniqueByEmailUsers || []);
      setEmailGroupMap(emailGroupMap);
      setError(null);
    } catch (err) {
      console.error('Error loading app users:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const updateUser = async (userId: string, updates: Partial<AppUser>) => {
    try {
      // SECURITY: changes to `active` MUST go through the admin edge functions
      // so that the corresponding auth.users row is also banned/unbanned and
      // existing sessions are revoked. A plain UPDATE on app_users would only
      // hide the user from the UI but still allow them to log in.
      if (typeof updates.active === 'boolean') {
        const fnName = updates.active
          ? 'admin-activate-user'
          : 'admin-deactivate-user';
        const { error: fnError } = await supabase.functions.invoke(fnName, {
          body: { app_user_id: userId },
        });
        if (fnError) throw fnError;
      }

      // Strip `active` from the regular update payload — the edge function
      // already persisted it.
      const { active: _ignored, ...rest } = updates;
      const restHasFields = Object.keys(rest).length > 0;

      let data: any = null;
      if (restHasFields) {
        const { data: updated, error: updateError } = await supabase
          .from('app_users')
          .update({
            ...rest,
            updated_at: new Date().toISOString(),
          })
          .eq('id', userId)
          .select()
          .single();
        if (updateError) throw updateError;
        data = updated;
      }

      // Atualiza a lista local
      setUsers((prev) =>
        prev.map((user) =>
          user.id === userId
            ? { ...user, ...(data ?? {}), ...(typeof updates.active === 'boolean' ? { active: updates.active } : {}) }
            : user
        )
      );

      return data ?? { id: userId, ...updates };
    } catch (err) {
      console.error('Error updating user:', err);
      throw err;
    }
  };

  const getUserById = async (userId: string): Promise<AppUser | null> => {
    try {
      const { data, error: fetchError } = await supabase
        .from('app_users')
        .select('*')
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      console.error('Error getting user by ID:', err);
      return null;
    }
  };

  const getCurrentAppUser = async (): Promise<AppUser | null> => {
    if (!currentUser) return null;

    try {
      const { data, error: fetchError } = await supabase
        .from('app_users')
        .select('*')
        .eq('auth_user_id', currentUser.id)
        .single();

      if (fetchError) throw fetchError;
      return data;
    } catch (err) {
      console.error('Error getting current app user:', err);
      return null;
    }
  };

  return {
    users,
    loading,
    error,
    loadUsers,
    updateUser,
    getUserById,
    getCurrentAppUser,
    emailGroupMap,
  };
}

export function useAppUser(userId: string | null) {
  const [user, setUser] = useState<AppUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const loadUser = async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('app_users')
        .select(`
          *,
          system_role:system_roles(id, name, code),
          department:departments(id, name, code),
          accessible_departments:user_department_access(
            department:departments(id, name)
          )
        `)
        .eq('id', userId)
        .single();

      if (fetchError) throw fetchError;
      
      // Fetch manager data separately if exists
      let managerData = null;
      if (data.manager_id) {
        const { data: manager } = await supabase
          .from('app_users')
          .select('id, display_name, first_name, last_name')
          .eq('id', data.manager_id)
          .single();
        managerData = manager;
      }
      
      const processedUser = {
        ...data,
        manager: managerData
      };
      
      setUser(processedUser);
      setError(null);
    } catch (err) {
      console.error('Error loading user:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUser();
  }, [userId]);

  return {
    user,
    loading,
    error,
    loadUser,
  };
}

// Simplified user type for select dropdowns
export interface AppUserForSelect {
  id: string;
  first_name: string | null;
  last_name: string | null;
  display_name: string | null;
  email: string | null;
}

// Hook to get active app_users for selection (e.g., linking to employees)
export function useActiveAppUsersForSelect() {
  const [users, setUsers] = useState<AppUserForSelect[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const { data, error } = await supabase
          .from('app_users')
          .select('id, first_name, last_name, display_name, email')
          .eq('active', true)
          .order('first_name');

        if (error) throw error;
        setUsers(data || []);
      } catch (err) {
        console.error('Error loading app users for select:', err);
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  return { users, loading };
}
